import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Briefcase, MapPin, Clock, Send, Upload, FileText, Image, CheckCircle, ChevronLeft } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function RecruitmentPublicPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [view, setView] = useState("list"); // list | detail | apply | success
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", message: "" });
  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [cvUploaded, setCvUploaded] = useState(null);
  const [photoUploaded, setPhotoUploaded] = useState(null);
  const [uploading, setUploading] = useState({ cv: false, photo: false });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/jobs").then(r => setJobs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const jobId = searchParams.get("job");
    if (jobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === jobId);
      if (job) { setSelectedJob(job); setView("detail"); }
    }
  }, [searchParams, jobs]);

  const openDetail = (job) => {
    setSelectedJob(job);
    setView("detail");
    setSearchParams({ job: job.id });
  };

  const openApply = () => {
    setForm({ name: "", email: "", phone: "", address: "", message: "" });
    setCvFile(null); setPhotoFile(null);
    setCvUploaded(null); setPhotoUploaded(null);
    setView("apply");
  };

  const backToList = () => {
    setView("list");
    setSelectedJob(null);
    setSearchParams({});
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("Ukuran file maksimal 5MB");
      return;
    }
    setUploading(u => ({ ...u, [type]: true }));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/public/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (type === "cv") {
        setCvUploaded(data);
        toast.success("CV berhasil diupload");
      } else {
        setPhotoUploaded(data);
        toast.success("Pas foto berhasil diupload");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload gagal");
    } finally {
      setUploading(u => ({ ...u, [type]: false }));
    }
  };

  const submitApp = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Nama, email, dan no. telepon wajib diisi");
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        cv_url: cvUploaded?.url || "",
        cv_filename: cvUploaded?.filename || "",
        photo_url: photoUploaded?.url || "",
        photo_filename: photoUploaded?.filename || "",
      };
      await api.post(`/jobs/${selectedJob.id}/apply`, payload);
      setView("success");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed w-full z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#262626]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Link to="/"><img src={LOGO} alt="FI" className="w-8 h-8 rounded-full" /></Link>
            <span className="font-heading text-sm font-bold uppercase tracking-wider">Karir</span>
          </div>
          <Link to="/" className="text-sm text-gray-400 hover:text-white flex items-center gap-1" data-testid="karir-back"><ArrowLeft size={14} /> Beranda</Link>
        </div>
      </nav>

      <div className="pt-20 pb-16 px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">

        {/* SUCCESS VIEW */}
        {view === "success" && (
          <div className="text-center py-20" data-testid="apply-success">
            <div className="w-16 h-16 rounded-full bg-green-900/30 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={32} className="text-green-400" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-3">Lamaran Terkirim!</h1>
            <p className="text-gray-400 max-w-md mx-auto mb-2">Terima kasih telah melamar posisi <span className="text-white font-semibold">{selectedJob?.title}</span>.</p>
            <p className="text-gray-500 text-sm mb-8">Tim kami akan meninjau lamaran Anda dan menghubungi melalui email/telepon.</p>
            <Button onClick={backToList} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10" data-testid="back-to-jobs">
              Lihat Lowongan Lainnya
            </Button>
          </div>
        )}

        {/* LIST VIEW */}
        {view === "list" && (
          <>
            <div className="mb-10 text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E60000] mb-2">KARIR</p>
              <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-tight" data-testid="karir-title">Bergabung Bersama Kami</h1>
              <p className="text-gray-400 mt-3 max-w-lg mx-auto">FI Barbershop Sekip membuka kesempatan bagi Anda yang ingin berkarir di industri barbershop & salon.</p>
            </div>

            {jobs.length === 0 ? (
              <div className="text-center py-16 bg-[#0F0F0F] border border-[#262626]">
                <Briefcase size={48} className="text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Saat ini tidak ada lowongan yang tersedia</p>
                <p className="text-gray-500 text-sm mt-1">Cek kembali nanti</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="jobs-list">
                {jobs.map(j => (
                  <div key={j.id} className="bg-[#0F0F0F] border border-[#262626] p-6 hover:border-[#E60000]/60 transition-colors cursor-pointer group" onClick={() => openDetail(j)} data-testid={`job-card-${j.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h2 className="font-heading text-xl font-bold uppercase text-white group-hover:text-[#FF1A1A] transition-colors">{j.title}</h2>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-400">
                          <span className="flex items-center gap-1"><MapPin size={14} />{j.location}</span>
                          <span className="flex items-center gap-1"><Clock size={14} />{j.type}</span>
                          {j.salary_range && <Badge variant="secondary" className="text-xs">{j.salary_range}</Badge>}
                        </div>
                        {j.description && <p className="text-gray-500 text-sm mt-3 line-clamp-2">{j.description}</p>}
                      </div>
                      <Button className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-9 text-xs shrink-0 ml-4" data-testid={`view-detail-${j.id}`}>
                        Detail
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* DETAIL VIEW */}
        {view === "detail" && selectedJob && (
          <div data-testid="job-detail">
            <button onClick={backToList} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-6 transition-colors" data-testid="back-to-list">
              <ChevronLeft size={16} /> Kembali ke Daftar Lowongan
            </button>

            <div className="bg-[#0F0F0F] border border-[#262626] p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <Badge variant="secondary" className="text-xs uppercase">{selectedJob.type}</Badge>
                {selectedJob.salary_range && <Badge className="bg-green-900/30 text-green-400 text-xs">{selectedJob.salary_range}</Badge>}
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-3">{selectedJob.title}</h1>
              <div className="flex items-center gap-3 text-sm text-gray-400 mb-6">
                <span className="flex items-center gap-1"><MapPin size={14} />{selectedJob.location}</span>
              </div>

              {selectedJob.description && (
                <div className="mb-6">
                  <h3 className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">Deskripsi Pekerjaan</h3>
                  <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">{selectedJob.description}</p>
                </div>
              )}

              {selectedJob.requirements && (
                <div className="mb-8">
                  <h3 className="text-xs font-bold uppercase text-gray-500 tracking-wider mb-2">Persyaratan</h3>
                  <p className="text-gray-300 text-sm whitespace-pre-line leading-relaxed">{selectedJob.requirements}</p>
                </div>
              )}

              <Button onClick={openApply} className="bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-11 px-8" data-testid="apply-btn">
                <Send size={16} className="mr-2" /> Lamar Sekarang
              </Button>
            </div>
          </div>
        )}

        {/* APPLY FORM */}
        {view === "apply" && selectedJob && (
          <div data-testid="apply-view">
            <button onClick={() => setView("detail")} className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-6 transition-colors" data-testid="back-to-detail">
              <ChevronLeft size={16} /> Kembali ke Detail
            </button>

            <div className="bg-[#0F0F0F] border border-[#262626] p-6 md:p-8">
              <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-white mb-1">Lamar: {selectedJob.title}</h2>
              <p className="text-gray-500 text-sm mb-6">Isi data diri dan upload dokumen yang diperlukan</p>

              <form onSubmit={submitApp} className="space-y-5" data-testid="apply-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300 text-xs font-bold uppercase">Nama Lengkap *</Label>
                    <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none focus:border-[#E60000]" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="apply-name" />
                  </div>
                  <div>
                    <Label className="text-gray-300 text-xs font-bold uppercase">Email *</Label>
                    <Input type="email" className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none focus:border-[#E60000]" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="apply-email" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-300 text-xs font-bold uppercase">No. Telepon / WhatsApp *</Label>
                    <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none focus:border-[#E60000]" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} required placeholder="08xxxxxxxxxx" data-testid="apply-phone" />
                  </div>
                  <div>
                    <Label className="text-gray-300 text-xs font-bold uppercase">Alamat</Label>
                    <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none focus:border-[#E60000]" value={form.address} onChange={e => setForm({...form, address: e.target.value})} placeholder="Kota, Provinsi" data-testid="apply-address" />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-300 text-xs font-bold uppercase">Pesan / Pengalaman Kerja</Label>
                  <textarea className="mt-1 w-full bg-[#1A1A1A] border border-[#262626] text-white p-3 rounded-none min-h-[100px] text-sm focus:border-[#E60000] focus:outline-none" value={form.message} onChange={e => setForm({...form, message: e.target.value})} placeholder="Ceritakan pengalaman kerja atau keahlian Anda..." data-testid="apply-message" />
                </div>

                {/* File Upload Section */}
                <div className="border border-[#262626] bg-[#0A0A0A] p-4">
                  <h3 className="text-xs font-bold uppercase text-gray-400 tracking-wider mb-4">Upload Dokumen</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* CV Upload */}
                    <div>
                      <Label className="text-gray-300 text-xs font-bold uppercase flex items-center gap-1"><FileText size={12} /> CV / Resume</Label>
                      <p className="text-gray-600 text-[10px] mt-0.5 mb-2">PDF, DOC, DOCX (maks 5MB)</p>
                      <label className="flex items-center gap-2 cursor-pointer bg-[#1A1A1A] border border-[#262626] hover:border-[#E60000]/50 p-3 transition-colors" data-testid="cv-upload-label">
                        <Upload size={16} className="text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-400 truncate">{uploading.cv ? "Mengupload..." : cvUploaded ? cvUploaded.filename : "Pilih file CV"}</span>
                        <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={e => { setCvFile(e.target.files[0]); handleFileUpload(e.target.files[0], "cv"); }} data-testid="cv-upload-input" />
                      </label>
                      {cvUploaded && <p className="text-xs text-green-400 mt-1 flex items-center gap-1"><CheckCircle size={10} /> {cvUploaded.filename}</p>}
                    </div>

                    {/* Photo Upload */}
                    <div>
                      <Label className="text-gray-300 text-xs font-bold uppercase flex items-center gap-1"><Image size={12} /> Pas Foto</Label>
                      <p className="text-gray-600 text-[10px] mt-0.5 mb-2">JPG, PNG, WEBP (maks 5MB)</p>
                      <label className="flex items-center gap-2 cursor-pointer bg-[#1A1A1A] border border-[#262626] hover:border-[#E60000]/50 p-3 transition-colors" data-testid="photo-upload-label">
                        <Upload size={16} className="text-gray-400 shrink-0" />
                        <span className="text-sm text-gray-400 truncate">{uploading.photo ? "Mengupload..." : photoUploaded ? photoUploaded.filename : "Pilih pas foto"}</span>
                        <input type="file" className="hidden" accept="image/*" onChange={e => { setPhotoFile(e.target.files[0]); handleFileUpload(e.target.files[0], "photo"); }} data-testid="photo-upload-input" />
                      </label>
                      {photoUploaded && (
                        <div className="mt-2">
                          <img src={photoUploaded.url} alt="Preview" className="w-16 h-16 object-cover border border-[#262626]" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={sending} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-11 text-sm" data-testid="apply-submit">
                  {sending ? "Mengirim Lamaran..." : "Kirim Lamaran"}
                </Button>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
